// Serienbrief (E-POST) im Serienmail-Assistenten, Schritt 3: dieselbe Nachricht per Post – an alle ohne E-Mail
// oder an alle. Je Empfänger ein Anschreiben auf dem Briefpapier (Platzhalter gefüllt), die PDF-/Bild-Anhänge
// der Serienmail in jedem Umschlag. Unabhängig vom E-Mail-Versand: eigener Start mit eigener Bestätigung.
import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle, CircleNotch, Envelope, Eye, WarningCircle } from '@phosphor-icons/react';
import { ImmoButton, SchalterZeile } from '../../../components/immobilien/ui';
import { useMobilModus } from '../../../lib/immobilien/mobil';
import { FELD, LABEL, Umschalter } from '../emails/bereich/Formular';
import { erzeugeBlankoBriefPdf, ladeBriefpapier, ladePostSchrift, type EingebetteteSchrift } from '../../../lib/dokumente/blankoBrief';
import { BRIEFE_JE_AUFRUF, EINSCHREIBEN, briefVorschau, ladeEpostEinstellung, sendeBriefe, type BriefAuftrag, type BriefOptionen, type BriefTeil, type EpostEinstellung } from '../../../lib/epost/epost';
import { serienBrief, waehleBriefEmpfaenger, type PostModus } from '../../../lib/epost/serienbrief';
import { formatiereDatumDeutsch } from '../../../lib/serienmail/api';
import type { Kandidat } from '../../../lib/serienmail/typen';
import { postFaehig } from '../dokumente/post/PostVersandDialog';
import { legeBriefInAkte } from '../../../lib/epost/ablage';

const heute = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Berlin' });

export function SerienbriefBereich({ alle, ohneEmail, betreff, html, anhaenge, absender }: {
  alle: Kandidat[];
  ohneEmail: Kandidat[];
  betreff: string;
  html: string;
  anhaenge: { name: string; path: string }[];
  absender: { name: string; email: string };
}) {
  const { mobil } = useMobilModus();
  const [einst, setEinst] = useState<EpostEinstellung | null>(null);
  const [modus, setModus] = useState<PostModus>('aus');
  const [optionen, setOptionen] = useState<BriefOptionen>({ farbe: false, duplex: true, einschreiben: '', sammelversand: false, premiumAdress: false });
  const [bestaetigung, setBestaetigung] = useState('');
  const [laeuft, setLaeuft] = useState<null | { fertig: number; gesamt: number }>(null);
  const [ergebnis, setErgebnis] = useState<null | { ok: boolean; text: string }>(null);
  const [vorschauLaeuft, setVorschauLaeuft] = useState(false);
  const [inAkte, setInAkte] = useState(true);

  useEffect(() => { void ladeEpostEinstellung().then(setEinst, () => setEinst(null)); }, []);

  const { briefe, ohneAnschrift } = useMemo(() => waehleBriefEmpfaenger(modus, alle, ohneEmail), [modus, alle, ohneEmail]);
  const anlagen: BriefTeil[] = useMemo(() => anhaenge.filter((a) => postFaehig('', a.name))
    .map((a) => ({ art: /\.pdf$/i.test(a.name) ? 'pdf' : 'bild', storage: { bucket: 'email-anhaenge', path: a.path }, name: a.name, mime: /\.png$/i.test(a.name) ? 'image/png' : /\.pdf$/i.test(a.name) ? 'application/pdf' : 'image/jpeg' })), [anhaenge]);
  const weg = anhaenge.length - anlagen.length;
  const datum = formatiereDatumDeutsch(new Date());

  async function auftrag(k: Kandidat, papier: string, schrift: EingebetteteSchrift): Promise<BriefAuftrag> {
    const b = serienBrief(k, html, betreff, absender, datum);
    const pdf = erzeugeBlankoBriefPdf({ empfaenger: b.anschrift, ort: 'Kirchhain', datum: heute(), betreff: b.betreff, text: b.text }, papier, true, schrift);
    const typ = k.owner_type === 'kontakt' || k.owner_type === 'mieter' || k.owner_type === 'eigentuemer' ? k.owner_type : null;
    return {
      empfaenger: b.empfaenger, anschrift_im_dokument: true, optionen, betreff: b.betreff || 'Serienbrief',
      teile: [{ art: 'pdf', base64: pdf.output('datauristring').replace(/^data:[^,]*,/, ''), name: 'Anschreiben.pdf' }],
      objekt_id: k.objekt_id, bezug_typ: 'serienbrief', bezug_id: null,
      empfaenger_typ: typ, empfaenger_id: typ ? k.owner_id : null,
      entwurf: { anschrift: b.anschrift, betreff: b.betreff, text: b.text, mitAnschreiben: true, optionen,
        anlagen: anlagen.map((a) => ({ name: a.name ?? 'Anlage', mime: a.mime ?? 'application/pdf', ...(a.storage ? { storage: a.storage } : {}) })) },
    };
  }

  const vorschau = async () => {
    if (!briefe[0]) return;
    setVorschauLaeuft(true); setErgebnis(null);
    try {
      const r = await briefVorschau(await auftrag(briefe[0], await ladeBriefpapier(), await ladePostSchrift()), anlagen);
      const bytes = Uint8Array.from(atob(r.pdf_base64), (c) => c.charCodeAt(0));
      window.open(URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' })), '_blank', 'noopener');
    } catch (e) { setErgebnis({ ok: false, text: (e as Error).message }); }
    finally { setVorschauLaeuft(false); }
  };

  const senden = async () => {
    setErgebnis(null);
    setLaeuft({ fertig: 0, gesamt: briefe.length });
    const batchId = Math.floor(Date.now() / 1000) % 2_000_000_000;
    let fertig = 0;
    let abgelegt = 0;
    try {
      const papier = await ladeBriefpapier();
      const schrift = await ladePostSchrift();
      for (let i = 0; i < briefe.length; i += BRIEFE_JE_AUFRUF) {
        // PDFs erst je Paket erzeugen – bei hunderten Empfängern sonst zu viel Speicher im Browser
        const kand = briefe.slice(i, i + BRIEFE_JE_AUFRUF);
        const paket = await Promise.all(kand.map((k) => auftrag(k, papier, schrift)));
        const r = await sendeBriefe(paket, anlagen, batchId, inAkte);
        if (inAkte) {
          for (const [j, br] of r.briefe.entries()) {
            const k = kand[j];
            if (!br.pdf_base64 || !k) continue;
            const art = k.owner_type === 'mieter' || k.owner_type === 'eigentuemer' || k.owner_type === 'kontakt' ? k.owner_type : null;
            const a = await legeBriefInAkte({ briefId: br.id, pdfBase64: br.pdf_base64, betreff: paket[j].betreff ?? 'Serienbrief', objektId: k.objekt_id,
              art, empfaengerId: k.owner_id, ordnerName: null, empfaengerName: k.name }).catch(() => ({ ok: false as const, grund: '' }));
            if (a.ok) abgelegt++;
          }
        }
        fertig += paket.length;
        setLaeuft({ fertig, gesamt: briefe.length });
        if (i + BRIEFE_JE_AUFRUF >= briefe.length) setErgebnis({ ok: true, text: `${fertig} Briefe an die Post übergeben${r.test ? ' – Testmodus, nichts wird gedruckt' : ''}. Stand je Empfänger unter „Briefe“.${inAkte ? ` ${abgelegt} in den Akten abgelegt.` : ''}` });
      }
    } catch (e) {
      setErgebnis({ ok: false, text: `Abgebrochen nach ${fertig} von ${briefe.length} Briefen: ${(e as Error).message}. Die ${fertig} bereits übergebenen gehen raus.` });
    } finally { setLaeuft(null); setBestaetigung(''); }
  };

  const aus = einst !== null && !einst.aktiv;
  const freigabe = modus !== 'aus' && briefe.length > 0 && bestaetigung.trim() === String(briefe.length) && !aus && !laeuft;
  const karte: React.CSSProperties = { borderRadius: 24, padding: mobil ? 14 : 18, gap: 12 };
  const titel: React.CSSProperties = { fontSize: 12, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--ink3)' };

  return (
    <section className="immo-glas flex flex-col min-w-0" style={karte} aria-label="Serienbrief per Post">
      <span className="flex items-center" style={{ ...titel, gap: 6 }}><Envelope size={14} weight="bold" />Zusätzlich per Post (E-POST)</span>
      <div className="self-start">
        <Umschalter hoehe={mobil ? 44 : 34} wert={modus} onChange={(m) => { setModus(m); setBestaetigung(''); setErgebnis(null); }}
          optionen={[{ key: 'aus', label: 'Kein Brief' }, { key: 'ohneEmail', label: `Ohne E-Mail (${ohneEmail.length})` }, { key: 'alle', label: `Alle per Post (${alle.length})` }]} />
      </div>
      <span style={{ fontSize: 12.5, color: 'var(--ink2)', lineHeight: 1.5 }}>
        Jeder Empfänger bekommt ein Anschreiben auf dem Briefpapier mit dem Text dieser Serienmail (Platzhalter gefüllt).
        PDF- und Bild-Anhänge liegen in jedem Umschlag. Der Brief geht unabhängig von der E-Mail raus – eigener Start unten.
      </span>
      {aus && <span role="status" style={{ fontSize: 12.5, color: 'var(--warn)' }}>Der Briefversand ist im Control Center ausgeschaltet.</span>}
      {einst && !aus && (
        <span role="status" style={{ fontSize: 12.5, fontWeight: 700, padding: '8px 12px', borderRadius: 12, alignSelf: 'flex-start',
          background: einst.modus === 'live' ? 'var(--bad-soft)' : 'var(--ok-soft)', color: einst.modus === 'live' ? 'var(--bad)' : 'var(--ok)' }}>
          {einst.modus === 'live' ? 'LIVE – alle Briefe werden gedruckt, zugestellt und kosten Porto.' : 'Testmodus – nichts wird gedruckt.'}
        </span>
      )}

      {modus !== 'aus' && (
        <>
          <span style={{ fontSize: 13, fontWeight: 700 }}>
            {briefe.length} {briefe.length === 1 ? 'Brief' : 'Briefe'}
            {anlagen.length ? ` · ${anlagen.length} ${anlagen.length === 1 ? 'Anlage' : 'Anlagen'} je Umschlag` : ''}
          </span>
          {weg > 0 && <span style={{ fontSize: 12.5, color: 'var(--warn)' }}>{weg} Anhang/Anhänge nicht druckbar (nur PDF, PNG, JPG) – bleiben im Brief weg.</span>}
          {ohneAnschrift.length > 0 && (
            <span style={{ fontSize: 12.5, color: 'var(--warn)' }}>
              {ohneAnschrift.length} ohne vollständige Anschrift – kein Brief: {ohneAnschrift.slice(0, 5).map((k) => k.name).join(', ')}{ohneAnschrift.length > 5 ? ' …' : ''}
            </span>
          )}

          <div className="flex flex-col" style={{ gap: 6 }}>
            <SchalterZeile klein an={!!optionen.duplex} onChange={(v) => setOptionen((o) => ({ ...o, duplex: v }))} label="Beidseitig drucken" />
            <SchalterZeile klein an={!!optionen.farbe} onChange={(v) => setOptionen((o) => ({ ...o, farbe: v }))} label="Farbdruck" />
            <SchalterZeile klein an={!!optionen.sammelversand} onChange={(v) => setOptionen((o) => ({ ...o, sammelversand: v }))} label="Sammelversand (Mengenrabatt)"
              zusatz="Die Post sammelt bis zur Mindestmenge und verschickt spätestens am Freitag – nicht für Briefe mit Frist." />
            <SchalterZeile klein an={!!optionen.premiumAdress} onChange={(v) => setOptionen((o) => ({ ...o, premiumAdress: v }))} label="PREMIUMADRESS"
              zusatz="Kostet extra: Bei Unzustellbaren meldet die Post die neue Anschrift (nicht bei Einschreiben)." />
            <SchalterZeile klein an={inAkte} onChange={setInAkte} label="Briefe als PDF in die Akten legen"
              zusatz="Je Empfänger in den Ordner im Objekt (sonst 01 Verwaltung/Mieter bzw. Eigentümer)." />
            <select value={optionen.einschreiben ?? ''} aria-label="Einschreiben" onChange={(e) => setOptionen((o) => ({ ...o, einschreiben: e.target.value as BriefOptionen['einschreiben'] }))}
              style={{ ...FELD, width: 'auto', minWidth: 220 }}>
              <option value="">Normaler Brief</option>
              {EINSCHREIBEN.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </div>

          <div>
            <ImmoButton groesse={44} icon={vorschauLaeuft ? CircleNotch : Eye} onClick={() => void vorschau()} disabled={!briefe.length || vorschauLaeuft}>
              Brief-Vorschau ({briefe[0]?.name ?? '–'})
            </ImmoButton>
          </div>

          <label className="flex flex-col" style={{ gap: 8 }}>
            <span style={{ ...LABEL, fontWeight: 600, color: 'var(--ink2)' }}>
              Zur Bestätigung die Briefzahl <b className="immo-zahl" style={{ color: 'var(--ink)' }}>{briefe.length}</b> eintragen:
            </span>
            <input type="text" inputMode="numeric" value={bestaetigung} onChange={(e) => setBestaetigung(e.target.value)} placeholder={String(briefe.length)}
              style={{ ...FELD, width: 160, fontWeight: 800, fontSize: mobil ? 16 : 14 }} />
          </label>
          <div className="flex flex-wrap items-center" style={{ gap: 12 }}>
            <ImmoButton art="akzent" groesse={48} icon={laeuft ? CircleNotch : Envelope} onClick={() => void senden()} disabled={!freigabe} style={{ opacity: freigabe ? 1 : 0.45 }}>
              {laeuft ? `${laeuft.fertig} / ${laeuft.gesamt} übergeben …` : `${briefe.length} ${briefe.length === 1 ? 'Brief' : 'Briefe'} an die Post`}
            </ImmoButton>
          </div>
        </>
      )}
      {ergebnis && (
        <span role={ergebnis.ok ? 'status' : 'alert'} className="flex items-start" style={{ gap: 6, fontSize: 13, fontWeight: 700, color: ergebnis.ok ? 'var(--ok)' : 'var(--bad)' }}>
          {ergebnis.ok ? <CheckCircle size={15} weight="fill" /> : <WarningCircle size={15} weight="fill" />}{ergebnis.text}
        </span>
      )}
    </section>
  );
}
